import moment from 'moment';
import { API_DATE_FORMAT, DD_MM_YYYY, MOMENT_LL } from 'constants/date-format';

export const getInclusiveDates = (d1: Date, d2: Date): string[] => {
  const dates = [moment(d1).format(API_DATE_FORMAT)];
  if (d1 === d2) return dates;

  do {
    const d = new Date(d1.setDate(d1.getDate() + 1));
    dates.push(moment(d).format(API_DATE_FORMAT));
  } while (new Date(d2) > d1);

  return dates;
};

export const getDateBreakdown = (date: Date): string[] => {
  const momentized = moment(date);
  return [
    momentized.format('DD'),
    momentized.format('MMMM'),
    momentized.format('YYYY'),
  ];
};

export const getHumanizedRange = (startDate: Date, endDate: Date): string => {
  const start = getDateBreakdown(startDate);
  const end = getDateBreakdown(endDate);
  const sameMonthYear = start[1] === end[1] && start[2] === end[2];
  return sameMonthYear
    ? `${start[1]} ${start[0]}-${end[0]}, ${start[2]}`
    : `${moment(startDate).format(MOMENT_LL)} - ${moment(endDate).format(
        MOMENT_LL,
      )}`;
};

export const momentFormat = (date?: Date, template = DD_MM_YYYY): string => {
  const safeDate = date ? new Date(date) : new Date();
  return moment(safeDate).format(template);
};

export const convertToDate = (date?: string | Date | null): Date | null => {
  return date && moment(date, API_DATE_FORMAT, true).isValid()
    ? new Date(date)
    : null;
};

export const dateTransform = (_value: Date, originalValue: Date): string => {
  return moment(originalValue, API_DATE_FORMAT, true).isValid()
    ? moment(originalValue).format(API_DATE_FORMAT)
    : '';
};

export const isDateSameDay = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

export const isBookingEnabled = (
  closedDates: Date[],
  from: Date,
  to: Date,
): boolean => {
  // Set hours to 0 so that comparison works in the following for loop
  const reservationStart = new Date(from);
  reservationStart.setHours(0);
  const reservationEnd = new Date(to);
  reservationEnd.setHours(0);

  if (from === undefined || to === undefined) {
    return false;
  }
  if (from >= to) {
    return false;
  }

  for (
    const day = new Date(reservationStart);
    day < new Date(reservationEnd);
    day.setDate(day.getDate() + 1)
  ) {
    if (closedDates.some((d: Date) => isDateSameDay(d, day))) {
      return false;
    }
  }

  return true;
};

/**
 * Formats a JavaScript Date object into API date format YYYY-MM-DD.
 * @param {Date} date - The input Date object to be formatted.
 * @returns {string | null} - A string representing the formatted date in "YYYY-MM-DD" format,
 *                            or null if the input date is invalid.
 */
export const formatToApiDateFormat = (date: Date): string | null => {
  if (isValidDate(date)) {
    const year = date.getFullYear().toString();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date
      .getDate()
      .toString()
      .padStart(2, '0');
    return `${year}-${month}-${day}`;
  } else {
    return null;
  }
};

const isValidDate = (date: Date): boolean => {
  return date && !isNaN(date.getTime());
};
